import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import type { ReactNode } from "react";

import { useGetCallLogQuery } from "services/suno-comms/call-logs";
import { type CallLog } from "services/suno-comms/types/call-log.type";

export function useCallLogDrawerProvider() {
  const [isOpen, setIsOpen] = useState(false);
  const [callLogId, setCallLogId] = useState<string | undefined>();

  const { data: callLog, isFetching } = useGetCallLogQuery(String(callLogId), {
    skip: !callLogId
  });

  const handleClose = useCallback(() => setIsOpen(false), []);
  const handleOpen = useCallback((id?: string) => {
    if (id) {
      setCallLogId(id);
    }
    setIsOpen(true);
  }, []);

  return {
    isOpen,
    callLog,
    isFetching,
    handleOpen,
    handleClose
  };
}

export const CallLogDrawerContext = createContext<{
  isOpen: boolean;
  isFetching: boolean;
  callLog?: CallLog;
  handleOpen: (id?: string) => void;
  handleClose: () => void;
} | null>(null);

type PatientDrawerProviderProps = {
  children?: ReactNode;
};

export const CallLogDrawerProvider = ({
  children
}: PatientDrawerProviderProps) => {
  const { isOpen, isFetching, callLog, handleOpen, handleClose } =
    useCallLogDrawerProvider();

  const value = useMemo(
    () => ({ isOpen, isFetching, callLog, handleOpen, handleClose }),
    [isOpen, isFetching, callLog, handleOpen, handleClose]
  );

  return (
    <CallLogDrawerContext.Provider value={value}>
      {children}
    </CallLogDrawerContext.Provider>
  );
};

export const useCallLogDrawer = () => {
  const context = useContext(CallLogDrawerContext);
  if (!context) {
    throw new Error(
      `useCallLogDrawer must be used within a CallLogDrawerProvider`
    );
  }

  return context;
};
